/**
 * Orchestrates chat turns: starts the SSE stream, folds events into the live
 * Zustand state, and commits finished turns into the react-query cache so the
 * persisted conversation and the live view never disagree.
 *
 * Lifecycle of a turn:
 *   start → (conversation, user_message, status/activity/sources/progress/delta)*
 *         → done      → committed to the cache immediately (real ids known)
 *         → verification → patched onto the committed message
 *         → stream closes → detail refetched (authoritative)
 *   error / stop / network failure → committed with local ids (if the conversation is
 *   known), detail marked stale but not refetched so the error card doesn't vanish.
 */
import { errorMessage } from '@/api/client'
import { conversationCache, queryKeys } from '@/api/queries'
import { streamChat } from '@/api/sse'
import type { ChatEvent, ChatEventMap, ChatRequest, ConversationDetail, Message, Meta, Source } from '@/api/types'
import { extractCitedIds } from '@/lib/citations'
import { currentPath, navigateTo } from '@/lib/navigation'
import { queryClient } from '@/lib/queryClient'
import {
  chatStore,
  conversationKey,
  isLocalId,
  localId,
  NEW_CHAT_KEY,
  selectTurn,
  type LiveAssistantMessage,
  type LiveTurn,
  type TurnMode,
} from './chatStore'
import { useUiStore } from './uiStore'

const controllers = new Map<string, AbortController>()
const deltaBuffers = new Map<string, string>()
const flushTimers = new Map<string, number>()
/** streamId → where its committed assistant message lives (for late `verification`). */
const committed = new Map<string, { conversationId: string; messageId: string; complete: boolean }>()

const DELTA_FLUSH_MS = 40

function meta(): Meta | undefined {
  return queryClient.getQueryData<Meta>(queryKeys.meta)
}

function announce(text: string) {
  useUiStore.getState().announce(text)
}

function emptyAssistant(createdAt: string): LiveAssistantMessage {
  return {
    clientId: localId(),
    id: null,
    content: '',
    sources: [],
    cited: null,
    activity: [],
    status: 'streaming',
    stage: 'thinking',
    statusText: null,
    error: null,
    gap_flagged: false,
    verification: null,
    feedback: null,
    created_at: createdAt,
  }
}

function truncateFrom(messages: Message[], messageId: string | null): Message[] {
  if (!messageId) return messages
  const index = messages.findIndex((m) => m.id === messageId)
  return index === -1 ? messages : messages.slice(0, index)
}

function mergeSources(existing: Source[], incoming: Source[]): Source[] {
  if (!incoming.length) return existing
  const ids = new Set(existing.map((s) => s.id))
  const merged = [...existing]
  for (const source of incoming) {
    if (!ids.has(source.id)) {
      merged.push(source)
      ids.add(source.id)
    }
  }
  return merged
}

// ---------------------------------------------------------------- start

type StartOptions = {
  conversationId: string | null
  mode: TurnMode
  message: string
  department: string | null
  editMessageId?: string
}

function startTurn(options: StartOptions): boolean {
  const { conversationId, mode, message, department, editMessageId } = options
  const key = conversationKey(conversationId)
  const existing = selectTurn(chatStore.get(), key)
  if (existing) {
    if (existing.assistant.status === 'streaming') return false
    chatStore.removeTurn(existing.streamId)
  }

  let truncateAt: string | null = null
  if (conversationId) {
    const detail = queryClient.getQueryData<ConversationDetail>(queryKeys.conversation(conversationId))
    if (mode === 'regenerate') {
      const last = detail?.messages.at(-1)
      if (last?.role === 'assistant') truncateAt = last.id
    } else if (mode === 'edit' && editMessageId) {
      truncateAt = editMessageId
    }
    if (truncateAt) {
      const cutAt = truncateAt
      conversationCache.setMessages(queryClient, conversationId, (messages) => truncateFrom(messages, cutAt))
    }
    conversationCache.touch(queryClient, conversationId)
  }

  const request: ChatRequest = {
    conversation_id: conversationId,
    message: mode === 'regenerate' ? '' : message,
    department,
  }
  if (mode === 'regenerate') request.regenerate = true
  if (mode === 'edit' && editMessageId) request.edit_message_id = editMessageId

  const now = new Date().toISOString()
  const turn: LiveTurn = {
    streamId: localId(),
    conversationId,
    mode,
    truncateAt,
    request,
    user: mode === 'regenerate' ? null : { clientId: localId(), id: null, content: message, created_at: now },
    assistant: emptyAssistant(now),
  }
  chatStore.addTurn(key, turn)
  if (conversationId) chatStore.setConversationActive(conversationId, true)
  void runTurn(turn)
  return true
}

async function runTurn(turn: LiveTurn) {
  const { streamId } = turn
  const controller = new AbortController()
  controllers.set(streamId, controller)
  let failure: string | null = null
  try {
    await streamChat(turn.request, (event) => handleEvent(streamId, event), controller.signal)
  } catch (error) {
    failure = errorMessage(error)
  } finally {
    controllers.delete(streamId)
  }
  flushDelta(streamId)
  finishTurn(streamId, controller.signal.aborted, failure)
}

// ---------------------------------------------------------------- events

function handleEvent(streamId: string, event: ChatEvent) {
  switch (event.event) {
    case 'conversation':
      onConversation(streamId, event.data)
      break
    case 'user_message': {
      const { id } = event.data
      const user = chatStore.get().turns[streamId]?.user
      if (user && id) {
        chatStore.setClientKey(id, user.clientId)
        chatStore.updateTurn(streamId, (turn) => (turn.user ? { user: { ...turn.user, id } } : {}))
      }
      break
    }
    case 'status':
      chatStore.updateAssistant(streamId, () => ({
        stage: event.data.stage,
        statusText: event.data.message,
      }))
      break
    case 'progress':
      chatStore.updateAssistant(streamId, () => ({ statusText: event.data.text }))
      break
    case 'activity':
      chatStore.updateAssistant(streamId, (a) => ({ activity: [...a.activity, event.data] }))
      break
    case 'sources':
      chatStore.updateAssistant(streamId, (a) => ({
        sources: mergeSources(a.sources, event.data.sources ?? []),
      }))
      break
    case 'delta':
      bufferDelta(streamId, event.data.text ?? '')
      break
    case 'done':
      flushDelta(streamId)
      onDone(streamId, event.data)
      break
    case 'verification':
      onVerification(streamId, event.data)
      break
    case 'error':
      flushDelta(streamId)
      chatStore.updateAssistant(streamId, () => ({
        status: 'error',
        error: event.data.message || 'Something went wrong while answering.',
        stage: null,
        statusText: null,
      }))
      break
  }
}

function onConversation(streamId: string, data: ChatEventMap['conversation']) {
  const turn = chatStore.get().turns[streamId]
  if (!turn) return
  const { id, title } = data
  const wasNew = !turn.conversationId
  chatStore.updateTurn(streamId, () => ({ conversationId: id }))
  chatStore.mapConversation(id, streamId)
  chatStore.setConversationActive(id, true)

  if (wasNew) {
    const now = new Date().toISOString()
    const summary = { id, title: title || 'New chat', pinned: false, created_at: now, updated_at: now }
    conversationCache.upsertSummary(queryClient, summary)
    if (!queryClient.getQueryData(queryKeys.conversation(id))) {
      queryClient.setQueryData<ConversationDetail>(queryKeys.conversation(id), { ...summary, messages: [] })
    }
    // Only move the user to /c/:id if they are still looking at this new chat. The
    // 'new' mapping is released by ChatPage once the route has changed (no blank frame).
    const ownsNewChat = chatStore.get().byConversation[NEW_CHAT_KEY] === streamId
    if (ownsNewChat && currentPath() === '/') {
      navigateTo(`/c/${encodeURIComponent(id)}`, { replace: true })
    } else if (ownsNewChat) {
      chatStore.clearNewChat()
    }
  } else if (title) {
    conversationCache.patchSummary(queryClient, id, { title })
  }
}

function bufferDelta(streamId: string, text: string) {
  if (!text) return
  deltaBuffers.set(streamId, (deltaBuffers.get(streamId) ?? '') + text)
  if (!flushTimers.has(streamId)) {
    flushTimers.set(
      streamId,
      window.setTimeout(() => flushDelta(streamId), DELTA_FLUSH_MS),
    )
  }
}

/** Deltas are batched so a fast stream re-renders the message at most ~25×/s. */
function flushDelta(streamId: string) {
  const timer = flushTimers.get(streamId)
  if (timer !== undefined) window.clearTimeout(timer)
  flushTimers.delete(streamId)
  const text = deltaBuffers.get(streamId)
  deltaBuffers.delete(streamId)
  if (text) chatStore.updateAssistant(streamId, (a) => ({ content: a.content + text }))
}

function onDone(streamId: string, data: ChatEventMap['done']) {
  chatStore.updateAssistant(streamId, (a) => ({
    id: data.message_id,
    content: typeof data.content === 'string' ? data.content : a.content,
    cited: Array.isArray(data.cited) ? data.cited : null,
    gap_flagged: Boolean(data.gap_flagged),
    status: 'complete',
    stage: null,
    statusText: null,
  }))
  if (meta()?.features.verification) chatStore.setPendingVerification(data.message_id, true)
  commitTurn(streamId, true)
  void queryClient.invalidateQueries({ queryKey: queryKeys.conversations })
  announce(`${meta()?.assistant_name ?? 'The assistant'} finished answering.`)
}

function onVerification(streamId: string, verification: ChatEventMap['verification']) {
  const info = committed.get(streamId)
  if (info) {
    conversationCache.patchMessage(queryClient, info.conversationId, info.messageId, { verification })
    chatStore.setPendingVerification(info.messageId, false)
  } else {
    chatStore.updateAssistant(streamId, () => ({ verification }))
  }
}

// ---------------------------------------------------------------- commit / finish

function toMessage(assistant: LiveAssistantMessage): Message {
  return {
    id: assistant.id ?? assistant.clientId,
    role: 'assistant',
    content: assistant.content,
    created_at: assistant.created_at,
    status: assistant.status === 'streaming' ? 'error' : assistant.status,
    error: assistant.error,
    sources: assistant.sources,
    cited: assistant.cited ?? extractCitedIds(assistant.content, assistant.sources),
    verification: assistant.verification,
    feedback: assistant.feedback,
    gap_flagged: assistant.gap_flagged,
    activity: assistant.activity,
  }
}

/**
 * Move a turn into the react-query cache. The live turn is removed on the next
 * tick, after react-query has notified its observers, so the view swaps live
 * rows for persisted rows (same React keys) without a blank frame.
 */
function commitTurn(streamId: string, complete: boolean): boolean {
  const turn = chatStore.get().turns[streamId]
  if (!turn?.conversationId) return false
  const conversationId = turn.conversationId
  const { user, assistant } = turn

  const userMessage: Message | null = user
    ? {
        id: user.id ?? user.clientId,
        role: 'user',
        content: user.content,
        created_at: user.created_at,
        status: 'complete',
      }
    : null
  const assistantMessage = toMessage(assistant)
  if (userMessage && user) chatStore.setClientKey(userMessage.id, user.clientId)
  chatStore.setClientKey(assistantMessage.id, assistant.clientId)

  const added = userMessage ? [userMessage, assistantMessage] : [assistantMessage]
  const addedIds = new Set(added.map((m) => m.id))
  const detailKey = queryKeys.conversation(conversationId)
  if (queryClient.getQueryData(detailKey)) {
    conversationCache.setMessages(queryClient, conversationId, (messages) => [
      ...truncateFrom(messages, turn.truncateAt).filter((m) => !addedIds.has(m.id)),
      ...added,
    ])
  } else {
    const now = new Date().toISOString()
    queryClient.setQueryData<ConversationDetail>(detailKey, {
      id: conversationId,
      title: 'New chat',
      pinned: false,
      created_at: now,
      updated_at: now,
      messages: added,
    })
  }

  committed.set(streamId, { conversationId, messageId: assistantMessage.id, complete })
  window.setTimeout(() => chatStore.removeTurn(streamId), 0)
  return true
}

function finishTurn(streamId: string, aborted: boolean, failure: string | null) {
  const turn = chatStore.get().turns[streamId]
  const info = committed.get(streamId)

  if (turn && !info) {
    // The stream ended without `done`.
    const status = turn.assistant.status
    if (status === 'streaming') {
      if (aborted) {
        chatStore.updateAssistant(streamId, () => ({ status: 'stopped', stage: null, statusText: null }))
        announce('Generation stopped.')
      } else {
        chatStore.updateAssistant(streamId, () => ({
          status: 'error',
          error: failure ?? 'The connection closed before the answer was finished.',
          stage: null,
          statusText: null,
        }))
        announce('The answer could not be completed.')
      }
    } else if (status === 'error') {
      announce('The answer could not be completed.')
    }

    const conversationId = chatStore.get().turns[streamId]?.conversationId
    if (conversationId) {
      commitTurn(streamId, false)
      // The backend may still be saving the partial/failed turn: mark stale, don't refetch now.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversation(conversationId),
        refetchType: 'none',
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations })
    }
    // Without a conversation id the failed turn stays live (keyed 'new') so it can be retried.
  }

  const done = committed.get(streamId)
  if (done) {
    chatStore.setPendingVerification(done.messageId, false)
    committed.delete(streamId)
    if (done.complete) {
      const otherTurnLive = Boolean(selectTurn(chatStore.get(), done.conversationId))
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversation(done.conversationId),
        refetchType: otherTurnLive ? 'none' : 'active',
      })
    }
  }

  const conversationId = turn?.conversationId ?? info?.conversationId ?? done?.conversationId
  if (conversationId && !hasOpenStream(conversationId, streamId)) {
    chatStore.setConversationActive(conversationId, false)
  }
}

function hasOpenStream(conversationId: string, exceptStreamId: string): boolean {
  const { turns } = chatStore.get()
  return Object.values(turns).some(
    (t) => t.streamId !== exceptStreamId && t.conversationId === conversationId && controllers.has(t.streamId),
  )
}

// ---------------------------------------------------------------- public API

export const chatController = {
  send(conversationId: string | null, message: string, department: string | null): boolean {
    const text = message.trim()
    if (!text) return false
    return startTurn({ conversationId, mode: 'send', message: text, department })
  },

  regenerate(conversationId: string, department: string | null): boolean {
    return startTurn({ conversationId, mode: 'regenerate', message: '', department })
  },

  edit(conversationId: string, messageId: string, message: string, department: string | null): boolean {
    const text = message.trim()
    if (!text) return false
    return startTurn({ conversationId, mode: 'edit', message: text, department, editMessageId: messageId })
  },

  /** Retry a failed answer, identified by its stable row key. */
  retry(conversationId: string | null, assistantKey: string, department: string | null): boolean {
    const key = conversationKey(conversationId)
    const live = selectTurn(chatStore.get(), key)
    if (live && live.assistant.clientId === assistantKey) {
      if (live.assistant.status === 'streaming') return false
      chatStore.removeTurn(live.streamId)
      if (live.conversationId && live.user?.id) {
        return startTurn({ conversationId: live.conversationId, mode: 'regenerate', message: '', department })
      }
      return startTurn({
        conversationId: live.conversationId,
        mode: live.mode === 'regenerate' ? 'regenerate' : 'send',
        message: live.user?.content ?? live.request.message,
        department: live.request.department ?? department,
      })
    }

    if (!conversationId) return false
    const detail = queryClient.getQueryData<ConversationDetail>(queryKeys.conversation(conversationId))
    const messages = detail?.messages ?? []
    const { clientKeys } = chatStore.get()
    const index = messages.findIndex((m) => (clientKeys[m.id] ?? m.id) === assistantKey)
    const failed = messages[index]
    const question = index > 0 ? messages[index - 1] : undefined
    if (failed && question?.role === 'user' && isLocalId(question.id)) {
      // The server never stored the question: drop the local pair and ask again.
      conversationCache.setMessages(queryClient, conversationId, (all) =>
        all.filter((m) => m.id !== question.id && m.id !== failed.id),
      )
      return startTurn({ conversationId, mode: 'send', message: question.content, department })
    }
    return startTurn({ conversationId, mode: 'regenerate', message: '', department })
  },

  /** Stop the generation in this conversation. Returns false if nothing was streaming. */
  stop(conversationId: string | null): boolean {
    const turn = selectTurn(chatStore.get(), conversationKey(conversationId))
    if (!turn || turn.assistant.status !== 'streaming') return false
    const controller = controllers.get(turn.streamId)
    if (!controller) return false
    flushDelta(turn.streamId)
    controller.abort()
    return true
  },

  /** Abort and forget every stream of a conversation (used when it is deleted). */
  abortConversation(conversationId: string) {
    const { turns } = chatStore.get()
    for (const turn of Object.values(turns)) {
      if (turn.conversationId === conversationId) {
        controllers.get(turn.streamId)?.abort()
        chatStore.removeTurn(turn.streamId)
      }
    }
    for (const [streamId, info] of committed) {
      if (info.conversationId === conversationId) controllers.get(streamId)?.abort()
    }
    chatStore.setConversationActive(conversationId, false)
  },

  /** Record feedback on a live (not yet committed) answer. */
  setLiveFeedback(messageId: string, feedback: Message['feedback']) {
    const { turns } = chatStore.get()
    for (const turn of Object.values(turns)) {
      if (turn.assistant.id === messageId) {
        chatStore.updateAssistant(turn.streamId, () => ({ feedback: feedback ?? null }))
      }
    }
  },

  /** "New chat": forget the previous new-chat turn mapping. */
  resetNewChat() {
    chatStore.clearNewChat()
  },
}
